const EPS = 1e-6;

function pointsClose(p1, p2, tol) {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y) < tol;
}

export default class PlacementManager {
  constructor(pattern) {
    this.pattern = pattern;
    this._height = 0;
    this._width = 0;
    this.pixelsPerBeat = -1;
    this.pixelsPerJuggler = -1;
    this.pointToEventMap = {};
  }

  height(height) {
    if (height) {
      this._height = height;
    }
    return this._height;
  }

  width(width) {
    if (width) {
      this._width = width;
    }
    return this._width;
  }

  get viewWidth() {
    return this.pixelsPerBeat * this.pattern.numBeats;
  }

  pointToEvent(point) {
    return this.pointToEventMap[this.pointToString(point)];
  }

  pointToString(point) {
    return point.x.toFixed(3) + "," + point.y.toFixed(3);
  }

  getThrowEndPoint(event) {
    const start = this.eventPositions[event.id()];
    let end = this.eventPositions[event.toEvent.id()];
    const roughX = start.x + (event.duration - 2) * this.pixelsPerBeat;
    while (Math.abs(roughX - end.x) > this.viewWidth/2) {
      end = {x: end.x + this.viewWidth * (end.x < roughX ? 1 : -1), y: end.y};
    }
    return end;
  }

  calculateArrowMidpoints(event) {
    const start = this.eventPositions[event.id()];
    const end = this.getThrowEndPoint(event);
    if (this.hasIntersectingPoints(start, end)) {
      const atTop = event.juggler == 0;
      const midX = (start.x + end.x) / 2 + (atTop ? -1 : 1) * Math.sqrt(Math.abs(start.y - end.y)) * 2;
      const midY = (start.y + end.y) / 2 + (atTop ? -1 : 1) * Math.sqrt(Math.abs(start.x - end.x)) * 2;
      return [{ x: midX, y: midY }];
    } else {
      return []; // No curve needed
    }
  }

  hasIntersectingPoints(start, end) {
    const tolerance = 30; // Pixels
    const isHoriVert = Math.abs(start.x - end.x) < EPS || Math.abs(start.y - end.y) < EPS;
    const minX = Math.min(start.x, end.x) - tolerance;
    const maxX = Math.max(start.x, end.x) + tolerance;
    const minY = Math.min(start.y, end.y) - tolerance;
    const maxY = Math.max(start.y, end.y) + tolerance;

    for (const point of Object.values(this.eventPositions)) {
      if (point.y < minY || maxY < point.y) continue;
      const testPoint = {x: point.x % this.viewWidth - this.viewWidth, y: point.y}; 
      while (testPoint.x <= maxX) {
        // Ignore start/end points and any points which overlap with them
        if (!pointsClose(testPoint, start, tolerance) && !pointsClose(testPoint, end, tolerance))  {
          // Check if the point is close to the line
          if (minX <= testPoint.x && testPoint.x <= maxX && (isHoriVert || this.pointToLineDistance(testPoint, start, end) < tolerance)) {
            return true;
          }
        }
        testPoint.x += this.viewWidth;
      }
    }
    return false; // No intersecting points found
  }

  // Calculates the perpendicular distance from a point to a line
  pointToLineDistance(point, lineStart, lineEnd) {
    const A = point.x - lineStart.x;
    const B = point.y - lineStart.y;
    const C = lineEnd.x - lineStart.x;
    const D = lineEnd.y - lineStart.y;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    if (lenSq != 0) {
      // in case of 0 length line
      param = dot / lenSq;
    }

    let xx, yy;

    if (param < 0) {
      xx = lineStart.x;
      yy = lineStart.y;
    } else if (param > 1) {
      xx = lineEnd.x;
      yy = lineEnd.y;
    } else {
      xx = lineStart.x + param * C;
      yy = lineStart.y + param * D;
    }

    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  calculatePixelMappings() {
    const maxTime = Math.max(...this.pattern.events.map(event => event.time));
    this.pixelsPerBeat = (this.width()) / (maxTime + 1);
    this.pixelsPerJuggler = this.height() / this.pattern.numJugglers;
  }

  calculateJugglerPositions() {
    this.calculatePixelMappings();
    const jugglerPositions = [];
    const yOffset = this.pixelsPerJuggler / 2;
    const xOffset = 20;
    for (let i = 0; i < this.pattern.numJugglers; i++) {
      jugglerPositions.push({
        x: xOffset,
        y: i * this.pixelsPerJuggler + yOffset,
      });
    }
    return jugglerPositions;
  }

  calculateEventPositions() {
    this.calculatePixelMappings();
    this.eventPositions = {};
    const yOffset = this.pixelsPerJuggler / 2;
    const xOffset = Math.max(this.pixelsPerBeat / 4, 100);
    for (const event of this.pattern.events) {
      const wrappedTime = event.time % this.pattern.numBeats;
      let numThrows = 0;
      for (const otherEvent of this.pattern.events) {
          if (otherEvent.juggler == event.juggler && Math.abs(otherEvent.time - wrappedTime) < 1e-6) {
              numThrows++;
          }
      }
      const HANDSHIFT = 45;
      const handShift = numThrows == 1 ? 0 : event.hand ? -HANDSHIFT : HANDSHIFT;
      const pos = {
          x: wrappedTime * this.pixelsPerBeat + xOffset,
          y: event.juggler * this.pixelsPerJuggler + yOffset + handShift,
      };
      this.eventPositions[event.id()] = pos;
      this.pointToEventMap[this.pointToString(pos)] = event;
    }
    return this.eventPositions;
  }
}
